// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import xngGaGeneralEventEvent from "@guestsupply/App/Handlers/GoogleAnalytics/GeneralEvents/GeneralEvents";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { sectionTitleFields } from "@guestsupply/App/Widgets/SectionTitle/SectionTitle";
import LazyImage, { LazyImageProps } from "@guestsupply/MobiusOverrides/LazyImage/LazyImage";
import { fontFamilyValues, GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Clickable from "@insite/mobius/Clickable";
import GridItem from "@insite/mobius/GridItem";
import { GridContainer } from "@insite/mobius/index";
import { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    viewMoreText = "viewMoreText",
    viewMoreLink = "viewMoreLink",
    border = "border",
    fontFamily = "fontFamily",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.viewMoreLink]: string | LinkFieldValue;
        [fields.viewMoreText]: string;
        [fields.border]: "top" | "bottom" | "none";
        [fields.fontFamily]: fontFamilyValues.GOTHAM | fontFamilyValues.BODONI;
    };
}
// XNG-CHANGES: Remove un-needed properties
const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    return {
        xBrandsAndSolutions: (state as CustomAppState).xBrandsAndSolutions,
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        // xng-change: add google event
        xGoogleAnalytics: state.xGoogleAnalytics,
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
    // xng-change: add google event
    xngGaGeneralEventEvent,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandListStyles {
    titleText?: TypographyProps;
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
    skipToContent?: SkipNavStyles;
    // XNG-Changes
    brandsWrapper?: InjectableCss;
}

export const listStyles: BrandListStyles = {
    titleText: {
        variant: "h3",
        transform: "inherit",
        css: css`
            border-width: 20px;
            text-align: center;
        `,
    },
    imageItem: {
        css: css`
            padding: 5px;
            justify-content: center;
        `,
    },
};

const styles = listStyles;

class BrandGallery extends React.Component<Props> {
    render() {
        const {
            fields,
            // XNG-CHANGES
            xBrandsAndSolutions,
            isMobileScreen,
            isTabletScreen,
        } = this.props;

        // XNG-CHANGES
        const brandList = xBrandsAndSolutions?.brandsAndSolutions?.item1;

        if (!brandList) {
            return null;
        }

        const sortedBrandList = [...(brandList.headerFeaturedBrands as any)]
            .sort((a, b) => a.title - b.title)
            .sort((a, b) => a.sortOrder - b.sortOrder);

        // XNG-CHANGES :CHANGE THE STRUCTURE
        return (
            <StyledWrapper {...styles.container} data-test-selector="brandGallery">
                {createWidgetElement("SectionTitle/SectionTitle", {
                    fields: {
                        title: this.props.fields.title,
                        viewMoreText: this.props.fields.viewMoreText,
                        viewMoreLink: this.props.fields.viewMoreLink,
                        border: this.props.fields.border,
                        fontFamily: this.props.fields.fontFamily,
                    },
                })}
                <VisuallyHidden as="h1">{translate("Explore available brands")}</VisuallyHidden>
                <StyledWrapper {...styles.brandsWrapper}>
                    {sortedBrandList?.slice(0, 10).map(brand => (
                        <Clickable
                            href={brand.detailPagePath}
                            {...styles.link}
                            key={brand.id}
                            // xng-change: add google event
                            onClick={() =>
                                this.props.xngGaGeneralEventEvent({
                                    eventType: GOOGLE_EVENTS.FEATURED_PARTNERS,
                                    includeEvent: this.props.xGoogleAnalytics.includeFeaturedPartnersEvent,
                                    clickText: brand.title,
                                    destination: brand.detailPagePath,
                                })
                            }
                        >
                            <LazyImage
                                width="230"
                                height="230"
                                {...styles.image}
                                src={brand.image}
                                altText={`${translate("Go to brand page")} ${brand.title}`}
                                customTransformations={{ default: "GUEST_Brand_Wx260" }}
                            />
                        </Clickable>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}
// XNG-CHANGS : GUEST-2655 : Removing isRequired
const brandGalleryTitleFields = [...sectionTitleFields];
brandGalleryTitleFields.forEach((field, index) => {
    if (field.displayName?.toLowerCase() === "title") {
        brandGalleryTitleFields[index].isRequired = false;
    }
});
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandGallery),
    definition: {
        group: "Brands",
        icon: "image",
        displayName: "Brand Gallery",
        allowedContexts: ["BrandsPage", "HomePage"],
        fieldDefinitions: [...(brandGalleryTitleFields as any)],
    },
};

export default widgetModule;
